import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, LinearProgress, IconButton,
  Chip, Paper, Fade, Stack, Tooltip
} from '@mui/material';
import {
  CameraAlt, ZoomIn, Close, Download, CloudUpload,
  Image as ImageIcon, DeleteForever,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { storage } from '../../config/firebase';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";
import { nanoid } from 'nanoid';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  borderRadius: theme.spacing(2),
  border: `2px dashed ${theme.palette.divider}`,
  position: 'relative',
  minHeight: 100,
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: 280,
  height: 280,
  margin: '0 auto',
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(0,0,0,0.18)' }
}));

const PlaceholderContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: 280,
  height: 280,
  margin: '0 auto',
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(2),
  border: `2px dashed ${theme.palette.grey[400]}`,
  transition: 'all 0.3s ease',
  '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.grey[100] }
}));

const PreviewImage = styled('img')({ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' });
const ZoomImage = styled('img')({ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 });

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    transform: 'scale(1.1)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  '&:hover': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    transform: 'scale(1.1)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
  },
}));

const ZoomIconButton = styled(IconButton)({
  position: 'absolute', top: 12, right: 12,
  backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
  backdropFilter: 'blur(8px)', border: '2px solid white',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', transform: 'scale(1.15)' },
});

const BadgeChip = styled(Chip)(({ theme }) => ({
  position: 'absolute', top: 12, left: 12,
  fontWeight: 'bold',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: '14px', height: 36, width: 36,
  borderRadius: '50%', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 10,
}));

const UploadButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4), fontSize: '16px', fontWeight: 600,
  borderRadius: theme.spacing(1), boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.25)' },
}));

const compressToBlob = (file, options = {}) => {
  const {
    maxDimension = 320,
    quality = 0.82,
    mimeType = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      const ratio = width / height;

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          width = maxDimension;
          height = Math.round(width / ratio);
        } else {
          height = maxDimension;
          width = Math.round(height * ratio);
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, width);
      canvas.height = Math.max(1, height);

      const ctx = canvas.getContext('2d');

      if (mimeType === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('No se pudo generar el Blob de la imagen'));
        },
        mimeType,
        mimeType === 'image/jpeg' ? quality : undefined
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error al cargar la imagen'));
    };

    img.src = url;
  });
};

const uploadBlob = (storageRef, blob, onProgress) =>
  new Promise((resolve, reject) => {
    const metadata = { contentType: blob.type };

    if (onProgress) {
      const task = uploadBytesResumable(storageRef, blob, metadata);
      task.on('state_changed',
        (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
      );
    } else {
      uploadBytes(storageRef, blob, metadata)
        .then((snap) => getDownloadURL(snap.ref))
        .then(resolve)
        .catch(reject);
    }
  });

export default function TakePictureCam({
  DocID,
  NoImg,
  detalle,
  segIndex,
  onImageUploaded,
  onImageDeleted,
  storagePath = 'Logo',
  maxCompressDimension = 280,
}) {
  const [openUpload, setOpenUpload] = useState(false);
  const [showZoom,   setShowZoom]   = useState(false);
  const [urlFull,    setUrlFull]    = useState(detalle?.Url || '');
  const [blobFull,   setBlobFull]   = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [progress,   setProgress]   = useState(0);
  const [uploading,  setUploading]  = useState(false);
  const [processing, setProcessing] = useState(false);
  const [deleting,   setDeleting]   = useState(false);

  useEffect(() => {
    setUrlFull(detalle?.Url || '');
  }, [detalle]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';
    setProcessing(true);
    setProgress(0);

    try {
      const supportedTypes = ['image/jpeg', 'image/png'];
      const mimeType = supportedTypes.includes(file.type) ? file.type : 'image/jpeg';

      const options = {
        maxDimension: maxCompressDimension,
        quality: mimeType === 'image/jpeg' ? 0.82 : undefined,
        mimeType,
      };

      const compressedBlob = await compressToBlob(file, options);

      setBlobFull(compressedBlob);

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const newPreviewUrl = URL.createObjectURL(compressedBlob);
      setPreviewUrl(newPreviewUrl);

      setOpenUpload(true);
    } catch (err) {
      console.error('Error al procesar imagen:', err);
      alert('Error al procesar la imagen:\n' + err.message);
    } finally {
      setProcessing(false);
    }
  }, [maxCompressDimension, previewUrl]);

  const handleSubmit = async () => {
    if (!blobFull) { alert('No hay imagen para subir'); return; }

    setUploading(true);
    setProgress(0);

    try {
      const storageRef = ref(storage, `/${storagePath}/${nanoid()}`);
      const url = await uploadBlob(storageRef, blobFull, setProgress);

      const imgObj = { Url: url, ChangeRec: new Date() };
      setUrlFull(url);
      onImageUploaded?.(imgObj);
      setOpenUpload(false);
    } catch (err) {
      console.error('Error al subir:', err);
      alert('Error al subir la imagen: ' + err.message);
    } finally {
      setUploading(false);
      setBlobFull(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta imagen? Esta acción no se puede deshacer.')) return;

    setDeleting(true);
    try {
      // Intentar borrar el archivo de Firebase Storage extrayendo la ruta desde la URL
      try {
        const urlObj  = new URL(urlFull);
        const pathRaw = decodeURIComponent(
          urlObj.pathname.split('/o/')[1]?.split('?')[0] || ''
        );
        if (pathRaw) {
          const fileRef = ref(storage, pathRaw);
          await deleteObject(fileRef);
        }
      } catch (storageErr) {
        // Si el archivo ya no existe en Storage, continuar igual
        console.warn('No se pudo borrar de Storage (puede que ya no exista):', storageErr.message);
      }

      setUrlFull('');
      onImageDeleted?.();
    } catch (err) {
      console.error('Error al eliminar imagen:', err);
      alert('Error al eliminar la imagen: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (!urlFull) return;
    try {
      const res  = await fetch(urlFull);
      const blob = await res.blob();
      const ext  = blob.type.split('/')[1] || 'jpg';
      const link = document.createElement('a');
      link.href     = URL.createObjectURL(blob);
      link.download = `imagen_${NoImg}_${Date.now()}.${ext}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Error al descargar:', err);
      alert('No se pudo descargar la imagen.');
    }
  };

  const inputId = `inputTag${NoImg}${segIndex !== undefined ? `-seg${segIndex}` : ''}`;

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <StyledPaper elevation={2}>

        <Stack spacing={2} alignItems="center">
          {urlFull ? (
            <ImageContainer>
              <PreviewImage src={urlFull} alt="foto" onClick={() => setShowZoom(true)} />
              <ZoomIconButton size="small" onClick={() => setShowZoom(true)}>
                <ZoomIn />
              </ZoomIconButton>
            </ImageContainer>
          ) : (
            <PlaceholderContainer>
              <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Sin imagen</Typography>
            </PlaceholderContainer>
          )}

          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <input
              type="file"
              id={inputId}
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <label htmlFor={inputId}>
              <Tooltip title="Tomar / Subir Foto" arrow>
                <ActionButton component="span" color="primary" disabled={processing || deleting}>
                  <CameraAlt />
                </ActionButton>
              </Tooltip>
            </label>

            {urlFull && (
              <Tooltip title="Ver imagen completa" arrow>
                <ActionButton onClick={() => setShowZoom(true)} color="primary" disabled={deleting}>
                  <ZoomIn />
                </ActionButton>
              </Tooltip>
            )}

            {urlFull && (
              <Tooltip title="Eliminar imagen" arrow>
                <DeleteButton
                  onClick={handleDelete}
                  color="error"
                  disabled={deleting}
                >
                  <DeleteForever />
                </DeleteButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <BadgeChip label={NoImg} />
      </StyledPaper>

      {/* Modal subida */}
      <Dialog
        fullScreen
        open={openUpload}
        onClose={() => !uploading && setOpenUpload(false)}
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Subir Imagen</Typography>
          <IconButton onClick={() => !uploading && setOpenUpload(false)} disabled={uploading}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
          {!uploading && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <UploadButton
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={handleSubmit}
                size="large"
              >
                Subir Imagen
              </UploadButton>
            </Box>
          )}

          <Box sx={{ maxWidth: 500, width: '100%', mx: 'auto', px: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 12, borderRadius: 2, bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%)'
                }
              }}
            />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              {uploading ? `${progress}%` : 'Listo para subir'}
            </Typography>
          </Box>

          {previewUrl && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
              <img
                src={previewUrl}
                alt="vista previa"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal zoom */}
      <Dialog
        open={showZoom}
        onClose={() => setShowZoom(false)}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ bgcolor: '#212529', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Imagen {NoImg} — Vista Completa</Typography>
          <IconButton color="inherit" onClick={() => setShowZoom(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ backgroundColor: '#000', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            {urlFull && <ZoomImage src={urlFull} alt="Vista completa" />}
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#212529', borderTop: '1px solid #444' }}>
          <Button onClick={() => setShowZoom(false)} variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
            Cerrar
          </Button>
          <Button onClick={handleDownload} variant="contained" startIcon={<Download />} color="primary">
            Descargar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}